import { computed, toRef } from "vue";
import { useOrganizationPositionsViewState } from "./useViewState";
import { useDropdownItems } from "@/composables/dropdown/useDropdownItems";
import {
  normalizeOrganizationPositionsViewSelection,
  shouldShowOrganizationPositionsViewSelector,
  withoutPreparingPositionTableColumns,
} from "@components/utils/preparingForOrganizationUi";
import { getPositionFilterChipLabel } from "@components/FilterPositionsModal/positionFilterPayload";

// Store callbacks for events
let eventCallbacks = {
  onCloseClick: null,
  onPreparingCloseClick: null,
  onPreparingAvatarsClick: null,
  onPreparingEffectiveDateClick: null,
  onRowChatterClick: null,
  onMenuClick: null,
  onPositionClick: null,
  onPreparePosition: null,
  onPrimaryAssign: null,
};

export function setEventCallbacks(callbacks) {
  eventCallbacks = { ...eventCallbacks, ...callbacks };
}

export function useTableConfig(viewStateParam = null) {
  // Use provided viewState or create new one (for backward compatibility)
  const viewState = viewStateParam || useOrganizationPositionsViewState();
  const dropdownItems = useDropdownItems();

  const initializeDropdownFlags = async () => {
    // Only initialize flags if the method exists (account version has it, storybook doesn't)
    if (dropdownItems.initializeFlags && typeof dropdownItems.initializeFlags === "function") {
      await dropdownItems.initializeFlags();
    }
  };

  const isNotEmpty = (val) => {
    if (Array.isArray(val)) {
      return val.length > 0;
    }
    if (val && typeof val === "object") {
      return Object.keys(val).length > 0;
    }
    return false;
  };

  function getEmployeeInfo(employee) {
    if (!employee || typeof employee !== "object") {
      return { photoUrl: "", firstName: "", lastName: "" };
    }

    const nameParts = (
      employee.firstName && employee.lastName
        ? `${employee.firstName} ${employee.lastName}`
        : employee.employeeName || employee.assigned_employee_name || ""
    ).split(" ");

    return {
      photoUrl:
        employee.photoUrl || employee.employeePicture || employee.assigned_employee_avatar || "",
      firstName: nameParts[0] || "",
      lastName: nameParts[1] || "",
    };
  }

  // Helper to extract position code from code_str (e.g., "#44 - Customer Service Representative" -> "#44")
  function getPositionCode(item) {
    // Check if already mapped format
    if (item.position?.position_code) {
      return item.position.position_code.toString();
    }
    // Handle raw API response format
    const codeMatch = item.code_str?.match(/#\d+/);
    return codeMatch ? codeMatch[0] : item.code?.toString() || "";
  }

  // Helper to get position name from either format
  function getPositionName(item) {
    return item.position?.position_name || item.name || "";
  }

  // Helper to get department name from either format.
  // The position belongs to the deepest node of the department / sub_department chain.
  function getDepartmentName(item) {
    let current = item.position?.department || item.department || null;
    let name = "";

    while (current) {
      if (current.name) name = current.name;
      current = current.sub_department ?? null;
    }

    return name;
  }

  // Helper to get roles count
  function getRolesCount(item) {
    return item.occupiedBy?.rolesCount || item.position?.roles_count || item.roles_count || 0;
  }

  // Helper to get responsibilities count
  function getResponsibilitiesCount(item) {
    return (
      item.occupiedBy?.responsibilityCount ||
      item.position?.responsibilities_count ||
      item.responsibilities_count ||
      0
    );
  }

  // Helper to build occupiedBy data from either format
  function normalizeEmployeeId(id) {
    if (id === null || id === undefined || id === "") return "";
    return String(id);
  }

  function getOccupiedByData(item) {
    // If already in mapped format
    if (item.occupiedBy) {
      const ob = item.occupiedBy;
      return {
        ...ob,
        employeeId: normalizeEmployeeId(ob.employeeId),
      };
    }
    // Map from raw API response
    const hasOccupied = item.assigned_primary !== null && item.assigned_primary !== undefined;
    return {
      employeeName: item.assigned_primary?.assigned_employee_name || "",
      employeePicture: item.assigned_primary?.assigned_employee_avatar || "",
      hasPrimaryOccupation: hasOccupied,
      hasVacant: item.vacancy > 0 && !hasOccupied,
      fteText: item.capacity || 1,
      effectiveDate: item.primary_position_effective_on || "",
      rolesCount: item.roles_count || 0,
      responsibilityCount: item.responsibilities_count || 0,
      directRespCount: item.direct_responsibility_assignments_count || 0,
      employeeId: normalizeEmployeeId(item.assigned_primary?.assigned_employee_id),
    };
  }

  // Helper to build preparingFor data from either format
  function getPreparingForData(item) {
    // If already in mapped format
    if (item.preparingFor) {
      const pf = item.preparingFor;
      const preparingEmployees = (pf.preparingEmployees || []).map((emp) => ({
        ...emp,
        employeeId: normalizeEmployeeId(emp.employeeId),
      }));
      return {
        ...pf,
        preparingEmployees,
        employeeId: normalizeEmployeeId(pf.employeeId),
      };
    }
    // Map from raw API response
    const preparingEmployees = (item.assigned_preparing_for || []).map((emp) => ({
      employeeName: emp.assigned_employee_name || "",
      employeePicture: emp.assigned_employee_avatar || "",
      firstName: emp.assigned_employee_name?.split(" ")[0] || "",
      lastName: emp.assigned_employee_name?.split(" ")[1] || "",
      photoUrl: emp.assigned_employee_avatar || "",
      effectiveDate: item.preparing_as_position_effective_on || "",
      employeeId: normalizeEmployeeId(emp.assigned_employee_id),
    }));

    return {
      preparingEmployees,
      roles: item.roles_count || 0,
      responsibilities: item.responsibilities_count || 0,
      directRespCount: item.direct_responsibility_assignments_count || 0,
      effectiveDate: item.preparing_as_position_effective_on || "",
      employeeId: preparingEmployees[0]?.employeeId || "",
      type: preparingEmployees.length > 0 ? "preparing" : "vacant",
    };
  }

  // Helper to get status
  function getStatus(item) {
    return item.status || "draft";
  }

  // Helper to get archived
  function getArchived(item) {
    return item.archived || false;
  }

  // Helper function to convert filter values to display strings for chips
  // Returns both display values and a mapping to original values for removal
  const getFilterDisplayValues = (filterValues) => {
    if (!Array.isArray(filterValues)) return { displayValues: [], valueMap: [] };

    const displayValues = [];
    const valueMap = []; // Maps display value back to original filter value

    filterValues.forEach((val) => {
      // If it's a string (status filter), return as is
      if (typeof val === "string") {
        if (val === "Do not Show") return;
        displayValues.push(getPositionFilterChipLabel(val));
        valueMap.push(val); // Map display value to itself
      } else if (val && typeof val === "object") {
        // If it's an object (radio button filter), return the value/label
        // Skip "No Filter" values as they shouldn't be displayed as chips
        const displayValue = val.value || val.label || "";
        // Don't show "No Filter" as a chip
        if (displayValue !== "No Filter") {
          displayValues.push(getPositionFilterChipLabel(displayValue));
          valueMap.push(val); // Map display value back to original object
        }
      }
    });

    return { displayValues, valueMap };
  };

  // Shared Position-card props for the Grid and Categorized views (MDP-615).
  const buildPositionCardProps = (item) => {
    const code = getPositionCode(item);
    const positionCapacity = {
      isInvalid: Boolean(item.is_invalid),
      directResponsibilitiesCount: item.direct_responsibilities_count || 0,
      notActiveDirectResponsibilitiesCount:
        item.not_active_direct_responsibilities_count || 0,
      validRolesCount: item.valid_roles_count || 0,
      invalidRolesCount: item.invalid_roles_count || 0,
      validResponsibilitiesCount: item.active_responsibilities_count || 0,
      capacity: item.capacity,
      allocation: item.vacancy,
    };
    const createdOn =
      item.position?.created_on || item.created_on
        ? new Date(item.position?.created_on || item.created_on)
        : undefined;

    return {
      class: "my-4 datatable-text-content",
      "data-position-id": item?.id,
      position_capacity: positionCapacity,
      position_name: getPositionName(item),
      position_code: code,
      department: getDepartmentName(item),
      ...(createdOn !== undefined && { created_on: createdOn }),
      hovered: true,
      active: item.status === "active",
      is_valid: item.is_valid !== false,
      invalidity_reason: item.invalidity_reason ? String(item.invalidity_reason) : "",
      archived: Boolean(item.archived),
    };
  };

  // The list payload has no occupied_str: occupied is capacity - vacancy, and two active
  // primary assignments on a 1 FTE position are the "2 x 0.5" case.
  const getCapacityAllocation = (item) => {
    const vacancy = item.vacancy ?? item.position?.vacancy;
    if (typeof item.capacity !== "number" || typeof vacancy !== "number") return "not";

    const occupied = Math.round((item.capacity - vacancy) * 100) / 100;
    if (occupied <= 0) return "not";
    if (occupied < 1) return "0.5";

    const primaryCount = (item.assigned_employees || []).filter(
      (employee) => employee.assigned_employee_commitment === 1
    ).length;
    return primaryCount > 1 ? "2x0.5" : "1";
  };

  // Grid view only (ER-12512): the card State follows the position status and the capacity
  // trigger follows the occupied portion. The Categorized view keeps buildPositionCardProps.
  const buildGridPositionCardProps = (item) => {
    const props = buildPositionCardProps(item);
    return {
      ...props,
      position_capacity: { ...props.position_capacity, allocation: getCapacityAllocation(item) },
      status: item.archived ? "archived" : item.status || "",
      capacityEditable: true,
    };
  };

  // --- MDP-615 Categorized view helpers -----------------------------------

  const categorizationState = () => viewState.state?.categorization || {};

  const isDepartmentColumnVisible = () =>
    categorizationState().departmentFlagEnabled === true;

  const isJobCategoryColumnVisible = () => {
    const c = categorizationState();
    return c.jobCategoriesOn === true && c.jobCategoryFlagEnabled === true;
  };

  const isJobFamilyColumnVisible = () => {
    const c = categorizationState();
    return c.jobFamiliesOn === true && c.jobFamilyFlagEnabled === true;
  };

  const isCategorizedRowReadOnly = (item) =>
    typeof viewState.isRowReadOnly === "function"
      ? viewState.isRowReadOnly(item)
      : Boolean(item?.archived) ||
        String(item?.status || "").toLowerCase() === "retired" ||
        categorizationState().canEditPositions === false;

  const getDepartmentTag = (item) =>
    typeof viewState.getCategorizedDepartment === "function"
      ? viewState.getCategorizedDepartment(item)
      : null;

  const getJobCategory = (item) =>
    typeof viewState.getCategorizedJobCategory === "function"
      ? viewState.getCategorizedJobCategory(item)
      : item?.job_category || null;

  const getJobFamily = (item) =>
    typeof viewState.getCategorizedJobFamily === "function"
      ? viewState.getCategorizedJobFamily(item)
      : item?.job_family || null;

  const getJobFamilyLevel = (item) =>
    typeof viewState.getCategorizedJobFamilyLevel === "function"
      ? viewState.getCategorizedJobFamilyLevel(item)
      : item?.job_family_level || null;

  /**
   * MDP-615 — Position / Department / Job Category / Job Family, editable from the
   * row. No Occupied / Preparing / Status / actions columns, and nothing sortable.
   * Columns are gated per Rule 4/13.
   */
  const buildCategorizedColumns = () => {
    const headers = [{ text: "Position", sortable: false, value: "name" }];
    const columns = [
      {
        field: "",
        component: "NewPositionCard",
        props: (item) => buildPositionCardProps(item),
        events: (item) => ({
          clickValidityBadge: () => {
            if (eventCallbacks.onPositionClick) {
              eventCallbacks.onPositionClick(item);
            }
          },
          positionClick: () => {
            if (eventCallbacks.onPositionClick) {
              eventCallbacks.onPositionClick(item);
            }
          },
        }),
      },
    ];

    if (isDepartmentColumnVisible()) {
      headers.push({
        text: "Department",
        sortable: false,
        value: "department",
        img: "department-icon-24.svg",
      });
      columns.push({
        field: "",
        component: "DepartmentTagInPositionForm",
        props: (item) => ({
          department: getDepartmentTag(item),
          removable: !isCategorizedRowReadOnly(item),
          readonly: isCategorizedRowReadOnly(item),
        }),
        events: (item) => ({
          assign: () => viewState.openCategorizedDepartmentPicker?.(item),
          remove: () => {
            const path = getDepartmentTag(item)?.path || [];
            viewState.openCategorizedUnassignDialog?.(
              "department",
              item,
              path[path.length - 1] || ""
            );
          },
        }),
      });
    }

    if (isJobCategoryColumnVisible()) {
      headers.push({
        text: "Job Category",
        sortable: false,
        value: "jobCategory",
        img: "category-icon.svg",
      });
      columns.push({
        field: "",
        component: "AssignedJobCategoryTag",
        props: (item) => {
          const category = getJobCategory(item);
          const score = category?.total_score;
          return {
            filled: !!category,
            jobCategoryName: category?.name || "",
            totalScore:
              score === null || score === undefined ? null : Number(score),
            readonly: isCategorizedRowReadOnly(item),
          };
        },
        events: (item) => ({
          assign: () => viewState.openCategorizedJobCategoryPicker?.(item),
          unassign: () =>
            viewState.openCategorizedUnassignDialog?.(
              "job-category",
              item,
              getJobCategory(item)?.name || ""
            ),
        }),
      });
    }

    if (isJobFamilyColumnVisible()) {
      headers.push({
        text: "Job Family",
        sortable: false,
        value: "jobFamily",
        img: "job-family/layers-three-01-24.svg",
      });
      columns.push({
        field: "",
        component: "AssignedJobFamilyTag",
        props: (item) => ({
          jobFamily: getJobFamily(item),
          level: getJobFamilyLevel(item),
          disabled: categorizationState().jobFamilyPicker?.assigning === true,
          readonly: isCategorizedRowReadOnly(item),
        }),
        events: (item) => ({
          assign: () => viewState.openCategorizedJobFamilyPicker?.(item),
          unassign: () =>
            viewState.openCategorizedUnassignDialog?.(
              "job-family",
              item,
              getJobFamily(item)?.name || ""
            ),
        }),
      });
    }

    return { headers, columns };
  };

  const tableParams = computed(() => {
    // IMPORTANT: Access reactive properties directly inside computed to track properly
    const currentFilterValues = [...(viewState.state.filters.values || [])];
    const currentSearchText = viewState.state.filters.searchText || "";
    const multipleFieldSearchValues = viewState.state.filters.multipleFieldSearchValues || [];

    // Check if any multiple field search has a value
    const hasMultipleFieldSearch = multipleFieldSearchValues.some(
      (f) => f.value && f.value.trim() !== ""
    );

    // Convert filter values to display strings for chips
    const { displayValues: filterDisplayValues, valueMap } =
      getFilterDisplayValues(currentFilterValues);

    // Store the value map on the viewState so removeFilters can access it
    viewState.state.filters._displayValueMap = valueMap;

    const hasCategorizedView =
      typeof viewState.hasCategorizedView === "function" &&
      viewState.hasCategorizedView();
    const effectiveViewSelection =
      viewState.config.positionsViewSelection === "Categorized" &&
      !hasCategorizedView
        ? "Grid"
        : normalizeOrganizationPositionsViewSelection(
            viewState.config.positionsViewSelection
          );
    const categorizedViewActive = effectiveViewSelection === "Categorized";

    const gridTableColumns = withoutPreparingPositionTableColumns(
      [
        {
          text: "Position",
          sortable: true,
          value: "name",
        },
        {
          text: "Occupied by",
          sortable: false,
          value: "occupiedBy",
          multiple: true,
          data: [
            {
              text: "Occupied by ",
              img: "employee-organization/primary-assignment-grey.svg",
            },
          ],
        },
        {
          text: "Preparing",
          sortable: false,
          value: "preparingFor",
          img: "icons/preparing.svg",
        },
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      [
        {
          field: "",
          component: "NewPositionCard",
          props: (item) => buildGridPositionCardProps(item),
          events: (item) => ({
            clickValidityBadge: () => {
              eventCallbacks.onPositionClick(item);
            },
            positionClick: () => {
              if (eventCallbacks.onPositionClick) {
                eventCallbacks.onPositionClick(item);
              }
            },
            capacityChange: (capacity) => viewState.handleCapacityChange?.(capacity, item),
          }),
        },
        {
          field: "",
          component: "PrimaryPositionAssignmentCard",
          props: (item) => {
            const occupiedBy = getOccupiedByData(item);

            // Map PrimaryPositionOccupationTag status logic to PrimaryPositionAssignmentCard type
            let type = "no primary assignment";
            if (occupiedBy.hasPrimaryOccupation) {
              type = "occupied";
            } else if (occupiedBy.hasVacant) {
              type = "vacant";
            }

            // Extract first and last name using the existing helper
            const employeeInfo = getEmployeeInfo({
              employeeName: occupiedBy.employeeName,
              employeePicture: occupiedBy.employeePicture,
            });

            return {
              class: "occupied_content my-4",
              type,
              photoUrl: employeeInfo.photoUrl,
              firstName: employeeInfo.firstName,
              lastName: employeeInfo.lastName,
              employeeId: occupiedBy.employeeId || "",
              positionCode: getPositionCode(item),
              positionName: getPositionName(item),
              positionId: item.position?.id ?? item.id,
              fte: occupiedBy.fteText || 1,
              effective_date: occupiedBy.effectiveDate || "",
              rolesCount: occupiedBy.rolesCount || 0,
              responsibilityCount: occupiedBy.responsibilityCount || 0,
              directRespCount: occupiedBy.directRespCount || 0,
              addHoverEffect: true,
              isHidden: !occupiedBy.effectiveDate,
            };
          },
          events: (item) => ({
            onClose: () => {
              viewState.onRelievePrimaryAssignment(item);
              if (eventCallbacks.onCloseClick) {
                eventCallbacks.onCloseClick(item);
              }
            },
            onHandleApplyDate: (effectiveDate) => {
              viewState.onPrimaryEffectiveDateClick(effectiveDate, item);
            },
            onPrimaryAssign: () => {
              if (eventCallbacks.onPrimaryAssign) {
                eventCallbacks.onPrimaryAssign(item);
              }
            },
            onRowChatterClick: () => {
              const occupiedBy = getOccupiedByData(item);
              if (occupiedBy.employeeId) {
                const employeeInfo = getEmployeeInfo({
                  employeeName: occupiedBy.employeeName,
                  employeePicture: occupiedBy.employeePicture,
                });
                viewState.openPositionChatterModal(
                  item,
                  "employee",
                  occupiedBy.employeeId,
                  occupiedBy.employeeName,
                  true,
                  employeeInfo
                );
              } else {
                viewState.openPositionChatterModal(item);
              }
            },
          }),
        },
        {
          field: "",
          component: "PreparingForPositionAssignmentCard",
          props: (item) => {
            const preparingFor = getPreparingForData(item);
            const occupiedBy = getOccupiedByData(item);
            const preparingEmployees = preparingFor.preparingEmployees || [];
            const employeeInfo =
              preparingEmployees.length > 0 ? getEmployeeInfo(preparingEmployees[0]) : {};

            const occupantInfo = occupiedBy.hasPrimaryOccupation
              ? getEmployeeInfo({
                  employeeName: occupiedBy.employeeName,
                  employeePicture: occupiedBy.employeePicture,
                })
              : null;

            return {
              class: "occupied_content my-4",
              positionName: getPositionName(item),
              positionCode: getPositionCode(item),
              positionId: item?.id ?? "",
              roles: preparingFor.roles || 0,
              responsibilities: preparingFor.responsibilities || 0,
              directRespCount: preparingFor.directRespCount || 0,
              nameUrl: preparingFor.nameUrl || "",
              occupiedList: occupantInfo ? [occupantInfo] : [],
              ...employeeInfo,
              effective_date: preparingFor.effectiveDate || "",
              addHoverEffect: true,
              employeeId: preparingFor.employeeId || "",

              type:
                preparingFor.type === "vacant"
                  ? "no prepare assignment"
                  : occupiedBy.hasPrimaryOccupation
                    ? "occupied"
                    : "vacant",
              isHidden: !preparingFor.effectiveDate,
            };
          },
          events: (item) => ({
            onClose: () => {
              viewState.onRelievePrepareAssignment(item);
              if (eventCallbacks.onPreparingCloseClick) {
                eventCallbacks.onPreparingCloseClick(item);
              }
            },
            onHandleApplyDate: (event) => {
              viewState.onPreparingEffectiveDateClick(event, item);
              if (eventCallbacks.onPreparingEffectiveDateClick) {
                eventCallbacks.onPreparingEffectiveDateClick(event, item);
              }
            },
            onPreparePosition: () => {
              if (eventCallbacks.onPreparePosition) {
                eventCallbacks.onPreparePosition(item);
              }
            },
            onRowChatterClick: () => {
              const preparingFor = getPreparingForData(item);
              if (preparingFor.employeeId) {
                const firstEmployee = preparingFor.preparingEmployees?.[0] || {};
                const employeeInfo = getEmployeeInfo(firstEmployee);
                viewState.openPositionChatterModal(
                  item,
                  "employee",
                  preparingFor.employeeId,
                  firstEmployee.employeeName || "",
                  true,
                  employeeInfo
                );
              } else {
                viewState.openPositionChatterModal(item);
              }
            },
          }),
        },
        {
          field: "",
          component: "Badge",
          props: (item) => ({
            status: getStatus(item),
            archived: getArchived(item),
            access: "has-access",
            showTooltip: false,
          }),
        },
        {
          field: "",
          component: "ChatterButton",
          props: () => ({
            class: "my-4",
          }),
          events: (item) => ({
            onClick: () => {
              viewState.openPositionChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "PositionLineMenu",
          props: (item) => {
            // Capitalize status for PositionLineMenu
            const status = item.status || "draft";
            const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
            return {
              position: item,
              status: capitalizedStatus,
              menuOpen: false,
            };
          },
          events: (item) => ({
            action: async (actionData) => {
              // Map PositionLineMenu action types to dropdown operations
              const operationMap = {
                activate: { value: "activate_action" },
                delete: { value: "delete_action" },
                rename: { value: "rename_action" },
                attachRole: { value: "attach_position_role" },
                archive: { value: "archive_action" },
                unarchive: { value: "unarchive_action" },
                retire: { value: "retire_action" },
                reactivate: { value: "reactivate_action" },
                clone: { value: "cloned_action" },
              };

              const operation = operationMap[actionData.type];
              if (operation) {
                await viewState.handlePositionDropdownOperation(operation, item);
                if (eventCallbacks.onMenuClick) {
                  eventCallbacks.onMenuClick({ item, operation });
                }
              }
            },
          }),
        },
      ]
    );

    const positionTableColumns = categorizedViewActive
      ? buildCategorizedColumns()
      : gridTableColumns;

    return {
      headers: positionTableColumns.headers,
      columns: positionTableColumns.columns,
      filters: {
        key: viewState.state.filters.key,
        values: filterDisplayValues, // Use display values for chips
        searchText: hasMultipleFieldSearch ? "multiple-field-search" : currentSearchText,
        rawValues: currentFilterValues, // Keep original structure for filtering logic
      },
      hideTopSection: viewState.config.hideTopSection || false,
      hideActiveNo: viewState.config.hideActiveNo || false,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "name",
      itemsPerPage: 10,
      activeRecordsCount: viewState.config.activeRecordsCount,
      actionLabel: viewState.config.actionLabel || "Add Position",
      hideGridView: true,
      title: viewState.config.title || "Positions Organization",
      orderingStorageKey: "organizationPositions.ordering",
      showPositionsViewSelector: shouldShowOrganizationPositionsViewSelector(),
      positionsViewSelection: effectiveViewSelection,
      showSearchPositionByMultipleFieldBox: viewState.config.showSearchPositionByMultipleFieldBox,
      searchPositionByMultipleFields: viewState.config.searchPositionByMultipleFields || [],
      hasCategorizedView,
      getRowStyle: (item) => {
        if (item?.status === "active") {
          return { backgroundColor: "#FFFFFF" }; //#ECFDF3
        } else if (item?.status === "retired") {
          return { backgroundColor: "#FFFFFF" }; //#e0e0e0
        }
        return { backgroundColor: "#FFFFFF" };
      },
    };
  });

  return {
    tableParams,
    initializeDropdownFlags,
  };
}
